import {
  parseThirdPartyResponsible,
  serializeThirdPartyResponsible,
} from '@risksmart-app/shared/src/forms/issue-assessments/thirdPartyResponsibleCodec';
import type { GetIssueAssessmentByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';

import type { ResponsibleThirdPartyToken } from '../../forms/issue-assessment-form/issueAssessmentSchema';

type Parent = {
  IssueId: string;
  ParentId: string;
  ParentType: Parent_Type_Enum;
};

type IssueParentWithThirdParty = {
  ParentId: string;
  ParentType?: Parent_Type_Enum;
  parent?: { ObjectType: Parent_Type_Enum } | null;
  thirdParty?: { Id: string; Title: string } | null;
};

type ThirdPartyOption = {
  Id: string;
  Title: string;
};

export const getAssociatedControlIds = (
  data: GetIssueAssessmentByParentIdQuery | undefined
) =>
  data?.issue_parent
    .filter((i) => i.parent?.ObjectType === Parent_Type_Enum.Control)
    .map((i) => i.ParentId) ?? [];

/**
 * Maps a multiselect's `{ value }[]` form field to a plain id array for the
 * save payload, falling back to the currently-loaded ids when the field is
 * absent.
 *
 * The `…Ids` link fields are TYPED as non-null arrays, but at submit they can
 * be `null` when their multiselect isn't the mounted control — e.g. a breach
 * flag is off, or the obligation/document/regulatory_breaches module is
 * disabled so the plain-text variant renders instead (WOR-130). A bare
 * `field.map(...)` then throws `Cannot read properties of null (reading 'map')`
 * client-side, before the mutation fires. Falling back to `fallback` (the
 * loaded ids) rather than `[]` also means a status-only save never silently
 * drops existing associations via the backend relationship diff-sync. A
 * genuine user-cleared multiselect submits `[]` (not null), so intentional
 * clears are preserved.
 */
export const resolveLinkIdValues = (
  field: { value: string }[] | null | undefined,
  fallback: string[]
): string[] => field?.map((a) => a.value) ?? fallback;

export const mapParentsToIds = (
  parents: Parent[],
  objectType: Parent_Type_Enum
) => {
  const ids =
    parents
      .filter((parent) => parent.ParentType === objectType)
      .map(({ ParentId }: { ParentId: string }) => ({
        value: ParentId,
      })) ?? [];

  return ids;
};

export const resolveRequestedResponsibleThirdPartyIds = ({
  requestedIds,
  requestedParentIds,
  currentIds,
}: {
  requestedIds: string[] | null | undefined;
  requestedParentIds: string[];
  currentIds: string[];
}): string[] => {
  if (Array.isArray(requestedIds)) {
    return requestedIds;
  }

  return requestedParentIds.length > 0 ? requestedParentIds : currentIds;
};

const isThirdPartyParent = (parent: IssueParentWithThirdParty): boolean =>
  parent.ParentType === Parent_Type_Enum.ThirdParty ||
  parent.parent?.ObjectType === Parent_Type_Enum.ThirdParty;

export const getResponsibleThirdPartyTokens = ({
  rawTypedNames,
  issueParents,
  isRegisteredIncluded,
  thirdPartyOptions = [],
  unavailableRegisteredLabel,
}: {
  rawTypedNames: string | null | undefined;
  issueParents: readonly IssueParentWithThirdParty[];
  isRegisteredIncluded: boolean;
  thirdPartyOptions?: readonly ThirdPartyOption[];
  unavailableRegisteredLabel: string;
}): ResponsibleThirdPartyToken[] => {
  const titlesById = new Map(
    thirdPartyOptions.map(({ Id, Title }) => [Id, Title])
  );
  const registeredTokens: ResponsibleThirdPartyToken[] = isRegisteredIncluded
    ? issueParents.filter(isThirdPartyParent).map((parent) => ({
        kind: 'registered' as const,
        value: parent.ParentId,
        label:
          parent.thirdParty?.Title ??
          titlesById.get(parent.ParentId) ??
          unavailableRegisteredLabel,
      }))
    : [];

  const typedTokens: ResponsibleThirdPartyToken[] = parseThirdPartyResponsible(
    rawTypedNames
  ).map((name) => ({
    kind: 'adHoc' as const,
    value: name,
    label: name,
  }));

  return [...registeredTokens, ...typedTokens].filter(
    (token, index, tokens) =>
      tokens.findIndex(
        (candidate) =>
          candidate.kind === token.kind && candidate.value === token.value
      ) === index
  );
};

type ResponsibleThirdPartyPayload = {
  ThirdPartyResponsible: string | null;
  ThirdPartyResponsibleIds?: string[];
};

export const mapResponsibleThirdPartiesToPayload = ({
  isCausedByThirdParty,
  isModuleEnabled,
  originalRegisteredIds = [],
  originalTypedNames,
  tokens,
}: {
  isCausedByThirdParty: boolean | null | undefined;
  isModuleEnabled: boolean;
  originalRegisteredIds?: string[];
  originalTypedNames: string | null | undefined;
  tokens: ResponsibleThirdPartyToken[];
}): ResponsibleThirdPartyPayload => {
  const typedNames = isCausedByThirdParty
    ? tokens
        .filter((token) => token.kind === 'adHoc')
        .map((token) => token.value)
    : [];
  const ThirdPartyResponsible = isCausedByThirdParty
    ? serializeThirdPartyResponsible(typedNames, originalTypedNames)
    : null;

  if (!isModuleEnabled) {
    return { ThirdPartyResponsible };
  }

  const registeredIds = isCausedByThirdParty
    ? tokens
        .filter((token) => token.kind === 'registered')
        .map((token) => token.value)
    : [];
  const registeredIdsChanged =
    registeredIds.length !== originalRegisteredIds.length ||
    registeredIds.some((id) => !originalRegisteredIds.includes(id));

  return {
    ThirdPartyResponsible,
    ...(registeredIdsChanged && {
      ThirdPartyResponsibleIds: registeredIds,
    }),
  };
};
